import type {
  PublicResearchMatchDetailMetric,
  PublicResearchMatchDetailResponse,
} from '~/features/worldcup-research/types'
import type { ImplementedLocale } from '~/lib/i18n/shared'
import { Card } from '@heroui/react'
import { useLocale, useTranslations } from 'use-intl'
import {
  formatMetricValue,
  formatValue,
  localizedDefinitionNote,
  metricDisplayName,
  metricShare,
  numberValue,
  StateText,
  translateOptional,
} from './components'
import { getPeriodMetricValue } from './utils'

export function MetricComparisonRow({ label, homeValue, awayValue, note }: {
  label: string
  homeValue: unknown
  awayValue: unknown
  note?: string
}) {
  const homeNumber = numberValue(homeValue)
  const awayNumber = numberValue(awayValue)
  const max = Math.max(homeNumber ?? 0, awayNumber ?? 0)
  const difference = homeNumber !== null && awayNumber !== null && homeNumber !== awayNumber
    ? Math.abs(homeNumber - awayNumber).toLocaleString()
    : ''

  return (
    <div className="rounded-md border border-divider bg-background/70 p-3">
      <div className="grid grid-cols-[minmax(4.5rem,0.75fr)_minmax(7rem,1fr)_minmax(4.5rem,0.4fr)] items-center gap-3">
        <div className="min-w-0">
          <div className="truncate text-sm font-bold">{label}</div>
        </div>
        <div className="grid grid-cols-[3.25rem_minmax(4.5rem,1fr)_3.25rem] items-center gap-2">
          <div className="text-right text-lg font-black tabular-nums">{formatValue(homeValue)}</div>
          <div className="grid grid-cols-2 gap-1">
            <div className="flex h-2 justify-end rounded-full bg-default-100">
              <div className="h-2 rounded-full bg-primary/55" style={{ width: metricShare(homeNumber, max) }} />
            </div>
            <div className="flex h-2 rounded-full bg-default-100">
              <div className="h-2 rounded-full bg-primary" style={{ width: metricShare(awayNumber, max) }} />
            </div>
          </div>
          <div className="text-lg font-black tabular-nums">{formatValue(awayValue)}</div>
        </div>
        <div className="text-right text-xs font-semibold text-muted">{difference}</div>
      </div>
      {note && <p className="mt-2 text-xs leading-5 text-muted">{note}</p>}
    </div>
  )
}

export function CoreComparisonPanel({ data, homeTeam, awayTeam, activeTab }: {
  data: PublicResearchMatchDetailResponse
  homeTeam: string
  awayTeam: string
  activeTab: 'ft' | '1h' | '2h'
}) {
  const t = useTranslations('ResearchWorkspace.matchDetail')
  const home = data.canonicalCore.home
  const away = data.canonicalCore.away

  const getVal = (key: string, originalVal: unknown, side: 'home' | 'away') => {
    return getPeriodMetricValue(key, activeTab, data.halfTimeRhythm, side, originalVal)
  }

  const rows = [
    { key: 'goals', label: t('core.goals'), home: getVal('goals', home?.goals, 'home'), away: getVal('goals', away?.goals, 'away') },
    { key: 'shotsTotal', label: t('core.shotsTotal'), home: getVal('shotsTotal', home?.shotsTotal, 'home'), away: getVal('shotsTotal', away?.shotsTotal, 'away') },
    { key: 'shotsOnTarget', label: t('core.shotsOnTarget'), home: getVal('shotsOnTarget', home?.shotsOnTarget, 'home'), away: getVal('shotsOnTarget', away?.shotsOnTarget, 'away') },
    { key: 'corners', label: t('core.corners'), home: getVal('corners', home?.corners, 'home'), away: getVal('corners', away?.corners, 'away') },
    { key: 'expectedGoals', label: t('core.expectedGoals'), home: getVal('expectedGoals', home?.expectedGoals, 'home'), away: getVal('expectedGoals', away?.expectedGoals, 'away') },
  ]
  const hasRows = rows.some(row => (row.home !== null && row.home !== undefined) || (row.away !== null && row.away !== undefined))

  return (
    <Card className="scroll-mt-20 p-5" variant="secondary">
      <Card.Header>
        <div>
          <h2 className="text-lg font-bold">{t('coreComparisonTitle')}</h2>
        </div>
      </Card.Header>
      <Card.Content>
        {!hasRows && <StateText>{t('emptyCore')}</StateText>}
        {hasRows && (
          <div className="space-y-2">
            <div className="grid grid-cols-[minmax(7rem,1fr)_minmax(11rem,1.3fr)_minmax(5rem,0.5fr)] gap-3 px-3 text-xs font-semibold uppercase text-muted">
              <div>{t('overviewTitle')}</div>
              <div className="text-center">{t('matchupLine', { homeTeam, awayTeam })}</div>
              <div className="text-right">{t('comparison')}</div>
            </div>
            {rows.map((row) => {
              if (row.key === 'expectedGoals' && row.home == null && row.away == null) {
                return (
                  <div key={row.key} className="rounded-md bg-warning/10 px-3 py-2 text-center text-xs text-warning">
                    {translateOptional(t, 'core.xgUnavailable', 'xG data unavailable for this match')}
                  </div>
                )
              }
              return (
                <MetricComparisonRow
                  key={row.key}
                  label={row.label}
                  homeValue={row.home}
                  awayValue={row.away}
                />
              )
            })}
          </div>
        )}
      </Card.Content>
    </Card>
  )
}

export function EnrichmentGroupPanel({ title, metrics, activeTab, halfTimeRhythm }: {
  title: string
  metrics: PublicResearchMatchDetailMetric[]
  activeTab: 'ft' | '1h' | '2h'
  halfTimeRhythm: PublicResearchMatchDetailResponse['halfTimeRhythm']
}) {
  const t = useTranslations('ResearchWorkspace.matchDetail')
  const locale = useLocale() as ImplementedLocale

  const mappedMetrics = metrics
    .map((metric) => {
      const homeValue = getPeriodMetricValue(metric.key, activeTab, halfTimeRhythm, 'home', metric.homeValue)
      const awayValue = getPeriodMetricValue(metric.key, activeTab, halfTimeRhythm, 'away', metric.awayValue)
      return { ...metric, homeValue, awayValue }
    })
    .filter((metric) => {
      if (activeTab === 'ft')
        return true
      const hasHome = metric.homeValue !== null && metric.homeValue !== undefined && metric.homeValue !== '' && metric.homeValue !== 0 && metric.homeValue !== '0' && metric.homeValue !== '0%'
      const hasAway = metric.awayValue !== null && metric.awayValue !== undefined && metric.awayValue !== '' && metric.awayValue !== 0 && metric.awayValue !== '0' && metric.awayValue !== '0%'
      return hasHome || hasAway
    })

  return (
    <Card className="scroll-mt-20 p-4" variant="secondary">
      <Card.Header>
        <div>
          <h2 className="text-lg font-bold">{title}</h2>
        </div>
      </Card.Header>
      <Card.Content className="space-y-3">
        {mappedMetrics.length === 0 && <StateText>{t('emptyGroup')}</StateText>}
        {mappedMetrics.map(metric => (
          <MetricComparisonRow
            key={metric.key}
            label={metricDisplayName(metric.key, locale === 'zh-CN' ? metric.labelZh : metric.labelEn, t)}
            homeValue={formatMetricValue(metric, metric.homeValue)}
            awayValue={formatMetricValue(metric, metric.awayValue)}
            note={localizedDefinitionNote(metric, locale, t)}
          />
        ))}
      </Card.Content>
    </Card>
  )
}
